<template>
  <div>
    <b-card bg-variant="light" class="mb-3">
      <b-row>
        <b-col md="4">
          <b-form-group label-cols="3" :label="`${$t('filter')}:`" class="mb-0">
            <b-input-group>
              <b-form-input
                v-model="searchFilter"
                :placeholder="$t('type-to-search')"
                debounce="300"
              />
              <b-input-group-append v-if="searchFilter">
                <b-btn @click="searchFilter = ''">&times;</b-btn>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            label-cols="5"
            :label="`${$t('brand.opportunities.opportunity-stage-filter')}:`"
            class="mb-0"
          >
            <multiselect
              v-model="stageFilter"
              :options="stageOptions"
              :multiple="true"
              :placeholder="$t('brand.opportunities.all-stages')"
              @input="filterUpdated"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label-cols="5" class="mb-0">
            <template #label>
              <span
                v-b-tooltip.hover="
                  $t('brand.opportunities.delivery-date-tooltip')
                "
              >
                {{ $t('brand.opportunities.delivery-date-label') }}
                <i
                  class="material-icons"
                  style="
                    font-size: 0.9rem;
                    vertical-align: middle;
                    cursor: help;
                  "
                >
                  info_outline
                </i>
              </span>
            </template>
            <div class="d-flex align-items-center">
              <date-picker :start="true" v-model="dateFrom" />
              <date-picker :end="true" v-model="dateTo" class="ml-2" />
              <small
                v-b-tooltip.hover="$t('brand.opportunities.job-status-tooltip')"
                class="text-muted ml-2"
                style="cursor: help"
              >
                <i
                  class="material-icons"
                  style="font-size: 0.9rem; vertical-align: middle"
                >
                  info_outline
                </i>
              </small>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <b-pagination
      v-if="totalRows > perPage"
      :total-rows="totalRows"
      :per-page="perPage"
      v-model="currentPage"
      align="center"
    />

    <b-table
      show-empty
      striped
      stacked="md"
      :items="filteredOpportunities"
      :busy="loading"
      :fields="tableFields"
      :per-page="perPage"
      :current-page="currentPage"
    >
      <template #cell(name)="{ item }">
        <strong
          role="button"
          class="clickable-text"
          :title="$t('brand.list.click-hint')"
          @click="handleNameClick(item)"
        >
          {{ item.name }}
        </strong>
      </template>
      <template #cell(stageName)="{ item }">
        <b-form-select
          :value="item.stageName"
          :options="stageSelectOptions"
          size="sm"
          class="stage-select"
          :class="`stage-${stageVariant(item.stageName)}`"
          @change="updateStage(item, $event)"
        />
      </template>
      <template #cell(totalJobs)="{ item }">
        <span
          role="button"
          class="clickable-text"
          @click="copyToClipboard(String(item.totalJobs || 0))"
        >
          {{ item.totalJobs || 0 }}
        </span>
      </template>
      <template #cell(totalRevenue)="{ item }">
        <span
          role="button"
          class="clickable-text"
          @click="copyToClipboard(String(item.totalRevenue || 0))"
        >
          {{ item.totalRevenue || 0 | currency }}
        </span>
      </template>
      <template #cell(totalCost)="{ item }">
        <span
          role="button"
          class="clickable-text"
          @click="copyToClipboard(String(item.totalCost || 0))"
        >
          {{ item.totalCost || 0 | currency }}
        </span>
      </template>
      <template
        #cell(_details)="{ toggleDetails, detailsShowing }"
        colspan="100%"
      >
        <button
          @click.stop="toggleDetails"
          style="font-size: 1.9rem; background: transparent; border: 0"
          class="material-icons"
        >
          {{ detailsShowing ? 'expand_less' : 'expand_more' }}
        </button>
      </template>
      <template #row-details="{ item }">
        <opportunity-metrics-table
          :metrics="getOpportunityMetrics(item.id)"
          :active-job-types="getOpportunityJobTypes(item.id)"
        />
      </template>
    </b-table>

    <b-pagination
      :total-rows="totalRows"
      :per-page="perPage"
      v-model="currentPage"
      align="center"
    />
  </div>
</template>

<script>
import axios from 'axios';
import Multiselect from 'vue-multiselect';
import DatePicker from '@gid/vue-common/components/DatePicker.vue';
import OpportunityMetricsTable from './OpportunityMetricsTable.vue';

export default {
  components: {
    Multiselect,
    DatePicker,
    OpportunityMetricsTable,
  },
  props: {
    brand: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      opportunities: [],
      metricsCache: {},
      searchFilter: '',
      stageFilter: [],
      stageOptions: [],
      dateFrom: null,
      dateTo: null,
      dateDebounce: null,
      clickState: { clicks: 0, timer: null },
      currentPage: 1,
      perPage: 10,
      loading: false,
      tableFields: [
        {
          label: this.$t('brand.opportunities.name'),
          key: 'name',
          sortable: true,
        },
        {
          label: this.$t('brand.opportunities.stage'),
          key: 'stageName',
          sortable: true,
        },
        {
          label: this.$t('brand.opportunities.total-jobs'),
          key: 'totalJobs',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          label: this.$t('brand.opportunities.total-revenue'),
          key: 'totalRevenue',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          label: this.$t('brand.opportunities.total-cost'),
          key: 'totalCost',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          label: '',
          key: '_details',
          class: 'text-center',
          tdClass: 'align-middle',
        },
      ],
    };
  },
  computed: {
    filteredOpportunities() {
      let result = this.opportunities;
      if (this.searchFilter) {
        const term = this.searchFilter.toLowerCase();
        result = result.filter((opp) => opp.name.toLowerCase().includes(term));
      }
      if (this.stageFilter && this.stageFilter.length > 0) {
        result = result.filter((opp) =>
          this.stageFilter.includes(opp.stageName),
        );
      }
      return result;
    },
    totalRows() {
      return this.filteredOpportunities.length;
    },
    stageSelectOptions() {
      return this.stageOptions.map((s) => ({ value: s, text: s }));
    },
  },
  watch: {
    searchFilter() {
      this.currentPage = 1;
    },
    dateFrom() {
      this.debouncedLoadData();
    },
    dateTo() {
      this.debouncedLoadData();
    },
  },
  created() {
    this.loadData();
  },
  beforeDestroy() {
    clearTimeout(this.dateDebounce);
  },
  methods: {
    debouncedLoadData() {
      clearTimeout(this.dateDebounce);
      this.dateDebounce = setTimeout(() => {
        this.currentPage = 1;
        this.loadData();
      }, 500);
    },
    handleNameClick(item) {
      this.clickState.clicks++;
      if (this.clickState.clicks === 1) {
        this.clickState.timer = setTimeout(() => {
          this.copyToClipboard(item.name);
          this.clickState.clicks = 0;
        }, 200);
      } else {
        clearTimeout(this.clickState.timer);
        window.open(`./data-ui/Opportunity/${item.id}`, '_blank');
        this.clickState.clicks = 0;
      }
    },
    async copyToClipboard(text) {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(text);
        } else {
          const el = document.createElement('textarea');
          el.value = text;
          document.body.appendChild(el);
          el.select();
          document.execCommand('copy');
          document.body.removeChild(el);
        }
        this.$bvToast.toast(`${text} copied`, {
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 1500,
          noCloseButton: true,
        });
      } catch (e) {
        console.error('Copy failed', e);
      }
    },
    async updateStage(item, newStage) {
      try {
        await axios.patch(`/data-api/opportunity/${item.id}`, {
          stageName: newStage,
        });
        item.stageName = newStage;
      } catch (error) {
        console.error('Failed to update stage', error);
      }
    },
    filterUpdated() {
      this.currentPage = 1;
    },
    stageVariant(stageName) {
      if (!stageName) return 'secondary';
      const lower = stageName.toLowerCase();
      if (lower.includes('closed won') || lower.includes('active'))
        return 'success';
      if (lower.includes('closed lost') || lower.includes('inactive'))
        return 'danger';
      if (lower.includes('qualification') || lower.includes('prospect'))
        return 'info';
      return 'primary';
    },
    async loadData() {
      this.loading = true;
      try {
        const params = {};
        if (this.dateFrom) params.dateFrom = this.dateFrom.substring(0, 10);
        if (this.dateTo) params.dateTo = this.dateTo.substring(0, 10);
        const response = await axios.get(
          `/data-api/opportunity/brand-metrics/${this.brand.id}`,
          { params },
        );
        this.processMetricsRows(response.data);
      } catch (error) {
        console.error('Error loading brand opportunities:', error);
      } finally {
        this.loading = false;
      }
    },
    processMetricsRows(rows) {
      // rows: flat SQL result with opportunityId, opportunityName, stageName,
      //        month, jobType, jobCount, totalRevenue, totalCost
      const oppMap = {};
      const metricsMap = {};

      for (const row of rows) {
        const oppId = row.opportunityId;

        // Build opportunity entry
        if (!oppMap[oppId]) {
          oppMap[oppId] = {
            id: oppId,
            name: row.opportunityName,
            stageName: row.stageName,
            isActive: row.isActive,
            totalJobs: 0,
            totalRevenue: 0,
            totalCost: 0,
          };
          metricsMap[oppId] = {
            months: {},
            totals: {},
            activeTypes: new Set(),
          };
        }

        const opp = oppMap[oppId];
        const metrics = metricsMap[oppId];
        const count = row.jobCount || 0;
        const revenue = row.totalRevenue || 0;
        const cost = row.totalCost || 0;
        const month = row.month;
        const jobType = row.jobType;

        // Skip rows with no jobs (LEFT JOIN produces null month/type)
        if (!month || !jobType) continue;

        // Accumulate opportunity-level totals
        opp.totalJobs += count;
        opp.totalRevenue += revenue;
        opp.totalCost += cost;

        // Monthly breakdown
        if (!metrics.months[month]) metrics.months[month] = {};
        metrics.months[month][jobType] = { count, revenue, cost };

        // Per-type totals
        if (!metrics.totals[jobType]) {
          metrics.totals[jobType] = { count: 0, revenue: 0, cost: 0 };
        }
        metrics.totals[jobType].count += count;
        metrics.totals[jobType].revenue += revenue;
        metrics.totals[jobType].cost += cost;

        metrics.activeTypes.add(jobType);
      }

      // Convert Sets to sorted arrays and store
      const cache = {};
      for (const [oppId, metrics] of Object.entries(metricsMap)) {
        cache[oppId] = {
          months: metrics.months,
          totals: metrics.totals,
          activeTypes: [...metrics.activeTypes].sort(),
        };
      }
      this.metricsCache = cache;

      // Build opportunities array sorted by name
      this.opportunities = Object.values(oppMap).sort((a, b) =>
        a.name.localeCompare(b.name),
      );

      // Build stage filter options
      const stages = new Set(
        this.opportunities.map((o) => o.stageName).filter(Boolean),
      );
      this.stageOptions = [...stages].sort();
    },
    getOpportunityMetrics(oppId) {
      return (
        this.metricsCache[oppId] || {
          months: {},
          totals: {},
          activeTypes: [],
        }
      );
    },
    getOpportunityJobTypes(oppId) {
      const metrics = this.metricsCache[oppId];
      return metrics ? metrics.activeTypes : [];
    },
  },
};
</script>

<style scoped lang="scss">
.clickable-text {
  color: #212529;
  cursor: pointer;
  transition: color 0.15s ease-in-out;

  &:hover {
    color: #000;
    text-decoration: underline;
  }
}

.stage-select {
  min-width: 130px;
  font-size: 0.85rem;
}

.stage-success {
  background-color: #d4edda;
  color: #155724;
}
.stage-danger {
  background-color: #f8d7da;
  color: #721c24;
}
.stage-info {
  background-color: #d1ecf1;
  color: #0c5460;
}
.stage-primary {
  background-color: #cce5ff;
  color: #004085;
}
.stage-secondary {
  background-color: #e2e3e5;
  color: #383d41;
}
</style>
